/* ----------------------------------------------------------------
   Kushface - Shop carousel (3D coverflow)
   Progressive enhancement: markup ships as a scroll-snap strip. When
   motion is allowed, JS upgrades it to a draggable 3D coverflow with
   one focused (front, center) slide. No deps. Mirrors lookbook.js
   conventions (pointer events, reduced-motion guard, init pattern).
   Add-to-cart is delegated to window.kfCart via [data-add-to-cart].
   ---------------------------------------------------------------- */

(function () {
  "use strict";

  var prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  var DRAG_THRESHOLD = 4; // px before a press becomes a drag (viewport-independent)
  var MIN_OPACITY = 0.15; // floor for the deepest visible slide (viewport-independent)

  // Coverflow geometry is viewport-dependent: the desktop-calibrated
  // ring shows only one slide on a phone. applyTune() recomputes these
  // from the live width. It runs at the top of measure() (on init AND
  // every resize), and render() reads the live values, so resizing
  // across a tier re-lays the ring with no extra listener.
  var ANGLE = 38; // deg of rotateY per step away from center
  var DEPTH = 220; // px pushed back per step
  var SIDE_SCALE = 0.7; // scale of non-focused slides
  var VISIBLE = 3; // slides shown either side of center
  var OPACITY_STEP = 0.28; // opacity lost per step back from center
  var SPACING_FACTOR = 0.62; // slide-width multiple → horizontal step

  function applyTune() {
    var w = window.innerWidth;
    if (w <= 480) {
      // Strongly recede the single neighbor so it reads as intentional
      // coverflow depth, not a glitchy image bleeding behind the product.
      ANGLE = 34; DEPTH = 260; SIDE_SCALE = 0.6;
      VISIBLE = 1; OPACITY_STEP = 0.6; SPACING_FACTOR = 0.62;
    } else if (w < 1024) {
      ANGLE = 32; DEPTH = 170; SIDE_SCALE = 0.74;
      VISIBLE = 2; OPACITY_STEP = 0.3; SPACING_FACTOR = 0.58;
    } else {
      ANGLE = 38; DEPTH = 220; SIDE_SCALE = 0.7;
      VISIBLE = 3; OPACITY_STEP = 0.28; SPACING_FACTOR = 0.62;
    }
  }

  function wrap(i, n) {
    return ((i % n) + n) % n;
  }

  function pad(x) {
    return x < 10 ? "0" + x : "" + x;
  }

  function initCarousel(rootEl) {
    var viewport = rootEl.querySelector("[data-carousel-viewport]");
    var stage = rootEl.querySelector("[data-carousel-stage]");
    if (!viewport || !stage) return;

    var slides = Array.prototype.slice.call(
      stage.querySelectorAll("[data-slide]")
    );
    var n = slides.length;
    if (n === 0) return;

    // Loading skeleton (runs in BOTH 3D and reduced-motion strip mode).
    // Images are eager + low priority, so this only briefly covers the
    // download; a cached/already-painted image shows no skeleton flash.
    slides.forEach(function (slide) {
      var media = slide.querySelector(".kf-shop__media");
      if (!media) return;
      var img = media.querySelector(".kf-shop__img");
      if (!img) return;
      if (img.complete && img.naturalWidth > 0) return;
      media.classList.add("is-loading");
      function settleImg() { media.classList.remove("is-loading"); }
      img.addEventListener("load", settleImg, { once: true });
      img.addEventListener("error", settleImg, { once: true });
    });

    var counter = rootEl.querySelector("[data-carousel-counter]");
    function updateCounter() {
      if (counter) counter.textContent = pad(active + 1) + " / " + pad(n);
    }

    // Reduced motion: keep the accessible scroll-snap strip, no 3D.
    if (prefersReducedMotion) return;

    viewport.classList.add("is-3d");

    var active = 0;
    var spacing = 1;

    function measure() {
      applyTune();
      var w = slides[0].getBoundingClientRect().width || 440;
      spacing = w * SPACING_FACTOR;
    }

    // Circular offset of slide i from a (possibly fractional) center
    // position, wrapped to the shortest path so the ring loops.
    function offsetOf(i, pos) {
      var o = (((i - pos) % n) + n) % n;
      if (o > n / 2) o -= n;
      return o;
    }

    // Render the ring at a continuous position. focusIndex gets the
    // meta/CTA; pass null mid-drag so nothing claims focus until it locks.
    function render(pos, focusIndex) {
      for (var i = 0; i < n; i++) {
        var slide = slides[i];
        var o = offsetOf(i, pos);
        var abs = Math.abs(o);

        if (abs > VISIBLE) {
          slide.style.opacity = "0";
          slide.style.pointerEvents = "none";
          slide.style.transform = "translate(-50%, -50%) translateZ(-1400px)";
          slide.style.zIndex = "0";
          slide.classList.remove("is-focused");
          slide.setAttribute("aria-hidden", "true");
          continue;
        }

        var k = Math.min(abs, 1); // eases 0 (center) -> 1 by one step out
        var tx = o * spacing;
        var tz = -abs * DEPTH;
        var ry = -o * ANGLE;
        var sc = 1 - k * (1 - SIDE_SCALE);
        var op = Math.max(MIN_OPACITY, 1 - abs * OPACITY_STEP);

        slide.style.opacity = String(op);
        slide.style.pointerEvents = "auto";
        slide.style.zIndex = String(n - Math.round(abs));
        slide.style.transform =
          "translate(-50%, -50%) translateX(" + tx +
          "px) translateZ(" + tz +
          "px) rotateY(" + ry +
          "deg) scale(" + sc + ")";

        if (focusIndex !== null && i === focusIndex) {
          slide.classList.add("is-focused");
          slide.removeAttribute("aria-hidden");
        } else {
          slide.classList.remove("is-focused");
          slide.setAttribute("aria-hidden", "true");
        }
      }
    }

    function settle() { render(active, active); updateCounter(); }
    function go(index) { active = wrap(index, n); settle(); }
    function next() { go(active + 1); }
    function prev() { go(active - 1); }

    // ---- free drag, snap-to-nearest on release ----
    var dragging = false;
    var moved = false;
    var startX = 0;
    var startActive = 0;
    var lastX = 0;
    var lastT = 0;
    var vx = 0; // pointer velocity px/ms (sign = pointer direction)
    var FLICK = 0.4; // px/ms above which a quick flick advances one

    stage.addEventListener("pointerdown", function (e) {
      // Never start a drag from a CTA/link: the click micro-movement would
      // cross DRAG_THRESHOLD, the browser would cancel the click (add-to-cart
      // never fires) and the ring would snap. Let the native click through.
      if (e.target.closest("button, a")) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startActive = active;
      lastX = e.clientX;
      lastT = e.timeStamp;
      vx = 0;
      stage.classList.add("is-grabbing"); // CSS drops the transition
      stopAuto();
      try { stage.setPointerCapture(e.pointerId); } catch (err) {}
    });

    stage.addEventListener("pointermove", function (e) {
      if (!dragging) return;
      var dxTotal = e.clientX - startX;
      if (!moved && Math.abs(dxTotal) > DRAG_THRESHOLD) moved = true;
      if (moved) e.preventDefault();
      var dt = e.timeStamp - lastT;
      if (dt > 0) vx = (e.clientX - lastX) / dt;
      lastX = e.clientX;
      lastT = e.timeStamp;
      // Drag right -> earlier products come to center (pos decreases).
      render(startActive - dxTotal / spacing, null);
    });

    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      stage.classList.remove("is-grabbing"); // transition back on
      if (!moved) { startAuto(); return; }
      var endX = e && typeof e.clientX === "number" ? e.clientX : lastX;
      var projected = startActive - (endX - startX) / spacing;
      // A fast flick nudges toward the next product in its direction.
      if (Math.abs(vx) > FLICK) projected += vx < 0 ? 0.5 : -0.5;
      active = wrap(Math.round(projected), n);
      settle(); // CSS transition animates the lock-to-nearest
      startAuto();
    }
    stage.addEventListener("pointerup", endDrag);
    stage.addEventListener("pointercancel", endDrag);
    stage.addEventListener("lostpointercapture", endDrag);
    stage.addEventListener("dragstart", function (e) { e.preventDefault(); });

    // ---- click resolves to a slide by geometry, not hit-testing ----
    // In a preserve-3d scene, document.elementsFromPoint is unreliable:
    // the full-bleed stage plane and the large focused slide overlap the
    // receded side slides, so the hit stack never resolves to the side
    // slide the user actually sees. Instead pick the front-most VISIBLE
    // slide whose transform-aware rect contains the point (highest
    // z-index wins ties) — exactly what the eye perceives.
    function rectHas(b, x, y) {
      return x >= b.left && x <= b.right && y >= b.top && y <= b.bottom;
    }
    function slideAtPoint(x, y) {
      var best = null;
      var bestZ = -1;
      for (var i = 0; i < n; i++) {
        var s = slides[i];
        if (parseFloat(getComputedStyle(s).opacity) === 0) continue; // off-ring
        if (!rectHas(s.getBoundingClientRect(), x, y)) continue;
        var z = parseInt(s.style.zIndex || "0", 10);
        if (z >= bestZ) { bestZ = z; best = i; }
      }
      return best;
    }
    stage.addEventListener("click", function (e) {
      if (moved) return; // was a drag, not a tap
      if (e.target.closest("button, a")) return; // let CTA / link work
      var idx = slideAtPoint(e.clientX, e.clientY);
      if (idx === null) return;
      if (idx !== active) {
        go(idx); // bring a side product to the front
        return;
      }
      // Already focused: a tap inside the product image opens the lightbox.
      var media = slides[idx].querySelector(".kf-shop__media");
      if (!media || !rectHas(media.getBoundingClientRect(), e.clientX, e.clientY)) {
        return;
      }
      var im = media.querySelector("img");
      if (!im) return;
      if (window.kfLightbox) {
        window.kfLightbox.open(
          im.getAttribute("data-zoom-src") || im.currentSrc || im.src,
          im.getAttribute("alt") || ""
        );
      }
    });
    slides.forEach(function (slide, i) {
      slide.addEventListener("keydown", function (e) {
        if ((e.key === "Enter" || e.key === " ") && i !== active) {
          e.preventDefault();
          go(i);
        }
      });
    });

    // ---- arrows ----
    var prevBtn = rootEl.querySelector("[data-carousel-prev]");
    var nextBtn = rootEl.querySelector("[data-carousel-next]");
    if (prevBtn) prevBtn.addEventListener("click", prev);
    if (nextBtn) nextBtn.addEventListener("click", next);

    // ---- keyboard on the viewport ----
    viewport.addEventListener("keydown", function (e) {
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
    });

    // ---- autoplay (opt-in, never under reduced motion) ----
    var autoplayMs = parseInt(rootEl.getAttribute("data-autoplay"), 10);
    var timer = 0;
    function startAuto() {
      if (!autoplayMs || timer) return;
      timer = window.setInterval(next, autoplayMs);
    }
    function stopAuto() {
      if (timer) { window.clearInterval(timer); timer = 0; }
    }
    if (autoplayMs) {
      startAuto();
      rootEl.addEventListener("pointerenter", stopAuto);
      rootEl.addEventListener("pointerleave", startAuto);
      rootEl.addEventListener("focusin", stopAuto);
      rootEl.addEventListener("focusout", startAuto);
    }

    // Pinch-zoom and the mobile URL-bar show/hide both fire `resize`
    // WITHOUT changing the layout width. Re-measuring then recomputes
    // `spacing` from a transient value and the side slides visibly jump
    // (and don't return to the same spot on zoom-out). Only relayout on
    // a real width change, never while pinch-zoomed, and debounce so a
    // burst of events coalesces into one settle.
    var lastW = window.innerWidth;
    var resizeT = 0;
    window.addEventListener("resize", function () {
      if (window.visualViewport && window.visualViewport.scale > 1) return;
      if (window.innerWidth === lastW) return;
      lastW = window.innerWidth;
      if (resizeT) window.clearTimeout(resizeT);
      resizeT = window.setTimeout(function () {
        resizeT = 0;
        measure();
        settle();
      }, 150);
    });

    measure();
    settle();
  }

  function init() {
    var carousels = document.querySelectorAll("[data-carousel]");
    for (var i = 0; i < carousels.length; i++) initCarousel(carousels[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
